// UC20 — geographic nomenclator (CUATM) editor for the System Administrator.
// Reference: docs/caiet-uc-spec.md §UC20-13 (hierarchical CUATM import, root =
// Republica Moldova), §UC20-14 (add, rename, change parent, activate/deactivate at
// any level) and §UC20-15 (edits kept as history), shaped after
// wireframes/react/src/pages/AdminLocalitati.tsx.
// The whole nomenclator arrives in one GET /admin/geography/tree: 1721 positions
// cannot be searched, filtered or re-parented while they are reachable only one
// expanded parent at a time. The tree starts collapsed; a query of two characters or
// more, or a filter, shows the matches together with their ancestors.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudCheckbox,
  MudFileInput,
  MudFileItem,
  MudIcon,
  MudInfoBox,
  MudTextInput,
  MudModal,
  MudToast,
  MudStepper,
  MudSelect,
  MudSpinner,
  MudTable,
  MudTag,
} from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey, isNormalizedApiError } from '../../app/api-client';
import {
  type ActiveFilter,
  EMPTY_CELL,
  EmptyRegisterMessage,
  FilterBar,
  onModalDismiss,
  onRowOpen,
  PageHeader,
  RowActionsMenu,
  useToast,
} from '@simsm/ui-shared';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { HistoryModal } from '../../features/history/HistoryModal';
import './geography.css';
import { GeographyPicker, type PickerOption } from './GeographyPicker';

// mud-table's own column/row types are not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
  width?: string;
}
type TableRowData = Record<string, string>;

interface GeoNode {
  id: string;
  parentId: string;
  kind: string;
  cuatmCode: string;
  // CUATM's second identifier; empty for a position added by hand, which has
  // none until the next edition of the classifier assigns one.
  statisticalCode: string;
  name: string;
  // CUATM's `DenumireRU`; empty when no Russian name is on record. Drawn as a
  // second line under the name, which is where the wireframe puts it
  // (AdminLocalitati.tsx:149).
  nameRu: string;
  active: boolean;
  rowVersion: number;
  depth: number;
  childCount: number;
  // Storage locations in this position's whole subtree.
  siteCount: number;
  // The active ones. While there is one, the position cannot be deactivated (UC20-16).
  activeSiteCount: number;
}

// A node with no parent is the root of the hierarchy. The caiet fixes what that
// root is (UC20-13, "rădăcina Republica Moldova") and the wireframe draws it as
// a fixed row carrying exactly one action; the app rendered it as an ordinary
// mutable row whose menu offered to deactivate the entire nomenclator.
function isRoot(node: GeoNode): boolean {
  return node.parentId === '';
}

const GEOGRAPHY_KEY = 'admin-geography';
const MIN_QUERY_LENGTH = 2;
const INDENT_PX = 20;

// The classifiers a position can be given by hand. COUNTRY is the seeded root and is
// never offered; the ones that cannot hold subdivisions are not offered as parents.
const EDITABLE_KINDS = [
  'REGION',
  'RAION',
  'MUNICIPALITY',
  'SECTOR',
  'CITY',
  'COMMUNE',
  'LOCALITY',
] as const;
const PARENT_KINDS = new Set([
  'COUNTRY',
  'REGION',
  'RAION',
  'MUNICIPALITY',
  'SECTOR',
  'CITY',
  'COMMUNE',
]);
const FILTER_KINDS = ['COUNTRY', ...EDITABLE_KINDS] as const;

type StatusFilter = '' | 'active' | 'inactive';

interface FormState {
  id: string | null;
  parentId: string;
  name: string;
  // Optional, and clearable: CUATM supplies one for every position it publishes,
  // a position added by hand has none until somebody types it, and a name that is
  // wrong has to be able to go away as well as change.
  nameRu: string;
  kind: string;
}

// The preview and the confirm come back in the same shape, from the same catalog
// code: `dryRun` says which of the two answered. `renamed` and `moved` are facets
// of `updated`, not a partition of it — a position can be both.
interface CuatmVanishedPosition {
  cuatmCode: string;
  name: string;
  kind: string;
}

// UC20-16: a position edited by hand that the file would change, field by field. The
// administrator ticks the ones whose manual values the file may overwrite.
interface CuatmManualConflict {
  cuatmCode: string;
  name: string;
  kind: string;
  fields: { field: string; current: string; incoming: string }[];
  overwritten: boolean;
}

// Absent from the file and kept active: active storage locations sit under it.
interface CuatmHeldPosition {
  cuatmCode: string;
  name: string;
  kind: string;
  activeSites: number;
}

interface CuatmImportSummary {
  received: number;
  created: number;
  updated: number;
  skipped: number;
  renamed: number;
  moved: number;
  vanished: number;
  deactivated: number;
  dryRun: boolean;
  vanishedSample: CuatmVanishedPosition[];
  manualConflicts: CuatmManualConflict[];
  manualKept: number;
  manualOverwritten: number;
  vanishedHeld: number;
  vanishedHeldSample: CuatmHeldPosition[];
}

interface CuatmImportError {
  rowIndex: number;
  column: string;
  code: string;
  message: string;
  params?: Record<string, string | number>;
}

interface CuatmImportResult {
  dryRun: boolean;
  imported: number;
  validRows: number;
  errors: CuatmImportError[];
  summary: CuatmImportSummary | null;
  format: 'cuatm' | 'simsm' | null;
}

// The row refusals the frontoffice can say in the reader's language. Any other code
// falls back to the server's English sentence rather than to a raw key.
const IMPORT_ERROR_CODES = new Set([
  'REQUIRED',
  'INVALID_KIND',
  'INVALID_STATUT',
  'DUPLICATE_CODE',
  'PARENT_NOT_FOUND',
  'PARENT_LISTED_LATER',
  'PARENT_CYCLE',
  'HEADER_UNRECOGNISED',
  'NO_ROWS',
  'NO_WORKSHEET',
]);

// Choose the file, read what the import will do, then confirm — the three steps
// MedicinesImportPage already ships. The middle one is the whole point here: a
// position that vanishes from CUATM is retired, and the administrator sees which
// ones before it happens rather than after.
function importStepStatus(index: number, current: number): 'pending' | 'current' | 'completed' {
  if (index < current) return 'completed';
  if (index === current) return 'current';
  return 'pending';
}

const CUATM_TEMPLATE_URL = '/api/v1/admin/geography/template.xlsx';

async function fetchTree(): Promise<GeoNode[]> {
  const { data } = await apiClient.get<{ nodes: GeoNode[] }>('/admin/geography/tree');
  return data.nodes ?? [];
}

function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

interface TreeIndex {
  byId: Map<string, GeoNode>;
  childrenOf: Map<string, GeoNode[]>;
}

function indexTree(nodes: GeoNode[]): TreeIndex {
  const byId = new Map<string, GeoNode>();
  const childrenOf = new Map<string, GeoNode[]>();
  for (const node of nodes) {
    byId.set(node.id, node);
    const bucket = childrenOf.get(node.parentId);
    if (bucket) bucket.push(node);
    else childrenOf.set(node.parentId, [node]);
  }
  return { byId, childrenOf };
}

function descendantsOf(index: TreeIndex, id: string): GeoNode[] {
  const out: GeoNode[] = [];
  const stack = [...(index.childrenOf.get(id) ?? [])];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) break;
    out.push(node);
    stack.push(...(index.childrenOf.get(node.id) ?? []));
  }
  return out;
}

function ancestorsOf(index: TreeIndex, node: GeoNode): GeoNode[] {
  const out: GeoNode[] = [];
  let current = index.byId.get(node.parentId);
  while (current) {
    out.push(current);
    current = index.byId.get(current.parentId);
  }
  return out;
}

// The formats the field takes; MUD writes the caption naming them in the page language.
const GEOGRAPHY_IMPORT_ACCEPT = '.xlsx,.csv';

export function GeographyPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [query, setQuery] = useState('');
  // The wireframe's first filter, and the one the register was missing: narrow
  // the tree to one position's subtree (AdminLocalitati.tsx `fParinte`, which
  // keeps the matches inside `descendenti(fParinte)`).
  const [parentFilter, setParentFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [form, setForm] = useState<FormState | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [historyNode, setHistoryNode] = useState<GeoNode | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<CuatmImportResult | null>(null);
  const [importOutcome, setImportOutcome] = useState<CuatmImportResult | null>(null);
  // The manually edited positions whose file values the administrator ticked (UC20-16).
  const [overwriteCodes, setOverwriteCodes] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const confirm = useConfirm();
  const toast = useToast();

  const tree = useQuery({ queryKey: [GEOGRAPHY_KEY, 'tree'], queryFn: fetchTree });
  const nodes = useMemo(() => tree.data ?? [], [tree.data]);
  const index = useMemo(() => indexTree(nodes), [nodes]);

  const debouncedQuery = useDebounced(query.trim(), 200);
  const searching = debouncedQuery.length >= MIN_QUERY_LENGTH;
  const filtering = searching || parentFilter !== '' || kindFilter !== '' || statusFilter !== '';

  const matches = useMemo(() => {
    if (!filtering) return new Set<string>();
    const needle = normalize(debouncedQuery);
    // The subtree, not the position itself: "everything under Chișinău" is what
    // the filter is for, and the position is reached by searching for it.
    const subtree = parentFilter
      ? new Set(descendantsOf(index, parentFilter).map((node) => node.id))
      : null;
    return new Set(
      nodes
        .filter(
          (node) =>
            (!subtree || subtree.has(node.id)) &&
            (!searching ||
              normalize(node.name).includes(needle) ||
              normalize(node.nameRu).includes(needle) ||
              node.cuatmCode.toLowerCase().includes(needle) ||
              node.statisticalCode.toLowerCase().includes(needle)) &&
            (kindFilter === '' || node.kind === kindFilter) &&
            (statusFilter === '' || (statusFilter === 'active') === node.active),
        )
        .map((node) => node.id),
    );
  }, [nodes, index, filtering, searching, debouncedQuery, parentFilter, kindFilter, statusFilter]);

  // Republica Moldova is the hierarchy's root, not a position in the classifier:
  // CUATM publishes 1721 rows and the wireframe's subtitle says so
  // (AdminLocalitati.tsx:112, `noduri.length` over an array the root is not in).
  const positionCount = nodes.reduce((total, node) => (isRoot(node) ? total : total + 1), 0);

  const visible = useMemo(() => {
    if (filtering) {
      const keep = new Set<string>();
      for (const id of matches) {
        const node = index.byId.get(id);
        if (!node) continue;
        keep.add(id);
        for (const ancestor of ancestorsOf(index, node)) keep.add(ancestor.id);
      }
      return nodes.filter((node) => keep.has(node.id));
    }
    const out: GeoNode[] = [];
    const walk = (parentId: string): void => {
      for (const child of index.childrenOf.get(parentId) ?? []) {
        out.push(child);
        // The root is a fixed row, not a disclosure: the wireframe lists the
        // first-level units under it at all times and gives it no chevron
        // (AdminLocalitati.tsx renders it outside the map, then walks from '').
        // Collapsing it left the administrator on a single row with the whole
        // nomenclator behind one click.
        if (isRoot(child) || expandedIds.has(child.id)) walk(child.id);
      }
    };
    walk('');
    return out;
  }, [nodes, index, matches, filtering, expandedIds]);

  function toggleExpand(id: string): void {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function invalidate(): void {
    queryClient.invalidateQueries({ queryKey: [GEOGRAPHY_KEY] });
  }
  // Successes go to the shared toast host — this used to be a page banner with a
  // `setTimeout` of its own, a second implementation of the host's job. Refusals
  // deliberately do NOT: `fail` writes a message the open modal renders beside
  // the control that caused it (the add/edit form, the import dialog), and a
  // corner toast would take the refusal away from the field it is about.
  function flash(title: string, name?: string): void {
    toast({ variant: 'success', title, text: name });
  }
  // The fallback is the caller's, per operation: an import that fails must not
  // tell the administrator that a rename failed.
  function fail(err: unknown, fallbackKey: string): void {
    setErrorMessage(t(apiErrorKey(err, fallbackKey)));
    setTimeout(() => setErrorMessage(null), 6000);
  }

  const createNode = useMutation({
    mutationFn: (input: FormState) =>
      apiClient
        .post<GeoNode>('/admin/geography', {
          parentId: input.parentId,
          kind: input.kind,
          name: input.name.trim(),
          nameRu: input.nameRu.trim(),
        })
        .then((r) => r.data),
    onSuccess: (created) => {
      invalidate();
      setExpandedIds((prev) => new Set([...prev, created.parentId]));
      setForm(null);
      flash(t('geography.toast.created'), created.name);
    },
    onError: (err) => fail(err, 'geography.error.create_failed'),
  });

  // A field edit and a re-parent are two different RPCs; the move needs the row
  // version the field edit just produced, so they run in sequence.
  const saveNode = useMutation({
    mutationFn: async (input: FormState & { id: string }) => {
      const current = index.byId.get(input.id);
      const { data: saved } = await apiClient.patch<GeoNode & { parentId: string }>(
        `/admin/geography/${input.id}`,
        {
          name: input.name.trim(),
          kind: input.kind,
          // The form owns this field, so it always states it: an empty value is
          // the administrator clearing a wrong Russian name, not an omission.
          nameRu: input.nameRu.trim(),
          // The PATCH replaces the row: omitting the code would blank it.
          cuatmCode: current?.cuatmCode ?? '',
          active: current?.active ?? true,
          expectedRowVersion: current?.rowVersion,
        },
      );
      if (current && current.parentId === input.parentId) return saved;
      const { data: moved } = await apiClient.post<GeoNode>(`/admin/geography/${input.id}/move`, {
        newParentId: input.parentId,
        expectedRowVersion: saved.rowVersion,
      });
      return moved;
    },
    onSuccess: (saved) => {
      invalidate();
      setForm(null);
      flash(t('geography.toast.saved'), saved.name);
    },
    onError: (err) => fail(err, 'geography.error.save_failed'),
  });

  const toggleActive = useMutation({
    mutationFn: (node: GeoNode) =>
      apiClient
        .post<GeoNode>(`/admin/geography/${node.id}/${node.active ? 'deactivate' : 'activate'}`, {
          expectedRowVersion: node.rowVersion,
        })
        .then((r) => r.data),
    onSuccess: (saved) => {
      invalidate();
      flash(
        saved.active ? t('geography.toast.activated') : t('geography.toast.deactivated'),
        saved.name,
      );
    },
    onError: (err) => {
      // A site activated after the tree was read: say why, not that it failed.
      if (isNormalizedApiError(err) && err.code === 'GEOGRAPHY_HAS_ACTIVE_SITES') {
        invalidate();
        setErrorMessage(t('geography.error.has_active_sites'));
        setTimeout(() => setErrorMessage(null), 6000);
        return;
      }
      fail(err, 'geography.error.toggle_failed');
    },
  });

  async function sendImport(file: File, dryRun: boolean): Promise<CuatmImportResult> {
    const body = new FormData();
    body.append('file', file);
    // Only the confirm carries the ticks: the preview lists every conflict as kept.
    if (!dryRun && overwriteCodes.size > 0) {
      body.append('overwriteManual', JSON.stringify([...overwriteCodes]));
    }
    // The confirm retires what the preview just named; the preview asks for
    // nothing, so a validation pass can never write.
    const params = dryRun ? '?dryRun=true' : '?deactivateMissing=true';
    const { data } = await apiClient.post<CuatmImportResult>(
      `/admin/geography/import${params}`,
      body,
    );
    return data;
  }

  const validateImport = useMutation({
    mutationFn: (file: File) => sendImport(file, true),
    onSuccess: (result) => {
      setImportPreview(result);
      setErrorMessage(null);
    },
    // A refused preview and a refused import are different events and say so: the
    // first rejected a file, the second stopped a write that may already have run.
    onError: (err) => {
      setImportPreview(null);
      fail(err, 'geography.error.validate_failed');
    },
  });

  const doImport = useMutation({
    mutationFn: (file: File) => sendImport(file, false),
    onSuccess: (result) => {
      setImportOutcome(result);
      if (result.summary) {
        invalidate();
        flash(
          t('geography.toast.imported'),
          t('geography.import.success', {
            created: result.summary.created,
            updated: result.summary.updated,
            deactivated: result.summary.deactivated,
          }),
        );
      }
    },
    onError: (err) => fail(err, 'geography.error.import_failed'),
  });

  function resetImport(): void {
    setImportFile(null);
    setImportPreview(null);
    setImportOutcome(null);
    setOverwriteCodes(new Set());
    setErrorMessage(null);
  }

  function toggleOverwrite(code: string, checked: boolean): void {
    setOverwriteCodes((prev) => {
      const next = new Set(prev);
      if (checked) next.add(code);
      else next.delete(code);
      return next;
    });
  }

  function importErrorMessage(err: CuatmImportError): string {
    // A code this page has no sentence for is named in the reader's language;
    // the server's own text is never shown (owner decision Q-1-03).
    return IMPORT_ERROR_CODES.has(err.code)
      ? t(`geography.import.error.${err.code}`, err.params ?? {})
      : t('uc20_import.error.unknown', { code: err.code });
  }

  // A blank value reads as the register's own empty cell, and a kind as its label.
  function changeValue(field: string, value: string): string {
    if (value === '') return EMPTY_CELL;
    return field === 'KIND' ? t(`geography.kind.${value}`) : value;
  }

  function openAdd(parent: GeoNode | null): void {
    setSubmitted(false);
    setErrorMessage(null);
    // Adding from the page header has no row to hang from; a single root is the
    // obvious parent, and anything else has to be picked before the form saves so a
    // second root can never be created by accident.
    const roots = index.childrenOf.get('') ?? [];
    const fallback = roots.length === 1 ? (roots[0]?.id ?? '') : '';
    // What goes directly under the country is a first-level unit, not a commune;
    // the wireframe opens the root's own "Adaugă unitate de nivel 1" on that
    // classifier too (AdminLocalitati.tsx `deschideAdd(null)`).
    const kind = parent?.kind === 'COUNTRY' ? 'RAION' : 'COMMUNE';
    setForm({ id: null, parentId: parent?.id ?? fallback, name: '', nameRu: '', kind });
  }
  function openEdit(node: GeoNode): void {
    setSubmitted(false);
    setErrorMessage(null);
    setForm({
      id: node.id,
      parentId: node.parentId,
      name: node.name,
      nameRu: node.nameRu,
      kind: node.kind,
    });
  }

  async function askDeactivate(node: GeoNode): Promise<void> {
    const subtree = descendantsOf(index, node.id).filter((child) => child.active);
    const ok = await confirm({
      title: t('geography.deactivate.confirm.title', { name: node.name }),
      body: (
        <>
          <p>
            {subtree.length === 0
              ? t('geography.deactivate.confirm.leaf', {
                  name: node.name,
                  kind: t(`geography.kind.${node.kind}`),
                })
              : t('geography.deactivate.confirm.subtree', {
                  name: node.name,
                  kind: t(`geography.kind.${node.kind}`),
                  count: subtree.length,
                })}
          </p>
          <p>{t('geography.deactivate.confirm.sites')}</p>
          {/* Reactivation restores this position alone — `setActive` cascades
              only downward on the way out, by design, so a subordinate that was
              already inactive is not silently switched back on. Saying "can be
              reactivated at any time" over a subtree was therefore a promise the
              system does not keep. */}
          <p>
            {subtree.length === 0
              ? t('geography.deactivate.confirm.reversible')
              : t('geography.deactivate.confirm.reversible_subtree')}
          </p>
        </>
      ),
      confirmLabel: t('geography.node.deactivate'),
      destructive: true,
    });
    if (ok) toggleActive.mutate(node);
  }

  const nameError = useMemo(() => {
    if (!form) return null;
    const trimmed = form.name.trim();
    if (trimmed.length === 0) return t('geography.error.name_required');
    const clash = nodes.some(
      (node) =>
        node.id !== form.id &&
        node.parentId === form.parentId &&
        normalize(node.name) === normalize(trimmed),
    );
    return clash ? t('geography.error.name_duplicate') : null;
  }, [form, nodes, t]);

  const parentError = form && form.parentId === '' ? t('geography.error.parent_required') : null;

  const parentCandidates = useMemo(() => {
    if (!form) return [];
    const blocked = new Set(
      form.id ? [form.id, ...descendantsOf(index, form.id).map((node) => node.id)] : [],
    );
    return nodes.filter(
      (node) => node.active && PARENT_KINDS.has(node.kind) && !blocked.has(node.id),
    );
  }, [form, nodes, index]);

  // The whole eligible list, in the tree's own reading order. The picker filters
  // it as the administrator types; nothing is truncated on the way in, which is
  // what the register's "50 din 1016" was.
  const parentOptions = useMemo<PickerOption[]>(
    () =>
      parentCandidates.map((node) => {
        const parent = index.byId.get(node.parentId);
        const kind = t(`geography.kind.${node.kind}`);
        return {
          id: node.id,
          name: node.name,
          detail: parent ? `${kind} · ${parent.name}` : kind,
          searchText: `${node.name} ${node.nameRu} ${node.cuatmCode} ${node.statisticalCode}`,
        };
      }),
    [parentCandidates, index, t],
  );

  function submitForm(): void {
    setSubmitted(true);
    if (!form || nameError || parentError) return;
    setErrorMessage(null);
    if (form.id) saveNode.mutate({ ...form, id: form.id });
    else createNode.mutate(form);
  }

  // The classifiers a position can be given by hand, in the wireframe's order
  // (TIPURI_CUATM). No `detail`: a classifier has nothing under it to say.
  const kindOptions = useMemo<PickerOption[]>(
    () =>
      EDITABLE_KINDS.map((kind) => {
        const label = t(`geography.kind.${kind}`);
        return { id: kind, name: label, detail: '', searchText: label };
      }),
    [t],
  );

  // The filter picker's list: the whole nomenclator, led by the entry that
  // clears the filter — the wireframe's `{ label: 'Toate pozițiile' }` at the
  // head of its own options.
  const filterParentOptions = useMemo<PickerOption[]>(
    () => [
      // "Toate pozițiile" is a CHOSEN option, not a placeholder — which is what
      // the two AGE selects beside it do with "Toate statuturile" and "Toate
      // tipurile", and why their triggers read in ink while this one read in
      // grey. MUD separates the two states: Default is an empty field showing its
      // placeholder, Filled is a field showing a value. A filter set to "all" is
      // filled, and it carries the checkmark in the list like any other choice.
      //
      // It could not be an option in the shape this control had before, where the
      // trigger WAS the query box and picking it wrote its label into the search
      // text. With the wireframe's trigger + panel split, it costs nothing.
      {
        id: '',
        name: t('geography.filter.parent_all'),
        detail: '',
        searchText: t('geography.filter.parent_all'),
      },
      ...nodes.map((node) => {
        const parent = index.byId.get(node.parentId);
        const kind = t(`geography.kind.${node.kind}`);
        return {
          id: node.id,
          name: node.name,
          detail: parent ? `${kind} · ${parent.name}` : kind,
          searchText: `${node.name} ${node.nameRu} ${node.cuatmCode} ${node.statisticalCode}`,
        };
      }),
    ],
    [nodes, index, t],
  );

  // Eight columns, as the wireframe draws them (AdminLocalitati.tsx) and as
  // docs/ui-shape.md §"Geographic nomenclature table width correction" requires.
  // The three identifier columns are slotted rather than left to mud-table's own
  // `.cell-text`, which is `nowrap` + `overflow: hidden` + `max-width: 100%` and
  // therefore ellipsises a code instead of asking the table for the room it
  // needs. Slotted content sets the cell's min-content width, so the table grows
  // past the container and the MUD wrapper's own `.table-scroll` — the only
  // horizontal scroll owner — takes over.
  const columns: TableColumn[] = [
    // Actions lead, as they do on every other register in the application
    // (`RegistrarsPage`, `SitesPage`, `MedicinesPage`, `AlertRulesPage` all put
    // `actions` first at a fixed narrow width). This table was the one that kept
    // them last, which on eight columns puts them past the horizontal scroll:
    // reaching a row's menu meant scrolling the table sideways first, and at the
    // 480px floor the menu sat off-screen entirely. Owner ruling, 2026-09-10.
    { key: 'actions', label: t('geography.column.actions'), width: '56px' },
    // No width: the column is sized by its cell's own `min-inline-size` (see
    // `.simsm-geo__name`). A percentage here resolves against a table width that
    // depends on content, which is what made it swing.
    { key: 'name', label: t('geography.column.name') },
    // Fixed, not left to the content. The label set is known and short, so an
    // auto-sized column takes its width from whichever rows happen to be on
    // screen — `țară · municipiu · raion` at the top level, then `comună / sat`
    // once anything is expanded — and every column after it shifted sideways on
    // each expand, search and filter. 8rem leaves 96px of content box, which is
    // what the wireframe gives it (`.geography-type`, min-width 120px against
    // 12px cell padding) and 10px more than the longest Romanian label needs.
    { key: 'kind', label: t('geography.column.kind'), width: '8rem' },
    { key: 'cuatmCode', label: t('geography.column.cuatm_code') },
    { key: 'statisticalCode', label: t('geography.column.statistical_code') },
    { key: 'childCount', label: t('geography.column.children'), align: 'end' },
    { key: 'siteCount', label: t('geography.column.sites'), align: 'end' },
    { key: 'status', label: t('geography.column.status') },
  ];

  // The three steps of the import, derived rather than stored: the file is chosen,
  // the preview says what the file will do, then the confirm reports what it did.
  // A parse error keeps the administrator at step 0 — there is nothing to preview.
  const importReport = importOutcome ?? importPreview;
  const importSummary = importReport?.summary ?? null;
  const importStep = importOutcome ? 2 : importPreview?.summary ? 1 : 0;
  const importBusy = validateImport.isPending || doImport.isPending;

  const rows: TableRowData[] = visible.map((node) => ({
    id: node.id,
    name: node.name,
    kind: t(`geography.kind.${node.kind}`),
    cuatmCode: node.cuatmCode || EMPTY_CELL,
    statisticalCode: node.statisticalCode || EMPTY_CELL,
    childCount: node.childCount === 0 ? EMPTY_CELL : String(node.childCount),
    siteCount: node.siteCount === 0 ? EMPTY_CELL : String(node.siteCount),
    status: '',
    actions: '',
  }));

  // The search term gets no chip: it clears from the box's own ×, which is the
  // wireframe's own rule for the shared bar (BaraFiltre.tsx:35).
  const activeFilters: ActiveFilter[] = [
    ...(parentFilter
      ? [
          {
            key: 'parent',
            label: t('geography.filter.parent'),
            value: (() => {
              const node = index.byId.get(parentFilter);
              return node ? `${node.name} · ${t(`geography.kind.${node.kind}`)}` : parentFilter;
            })(),
            onRemove: () => setParentFilter(''),
          },
        ]
      : []),
    ...(kindFilter
      ? [
          {
            key: 'kind',
            label: t('geography.filter.kind'),
            value: t(`geography.kind.${kindFilter}`),
            onRemove: () => setKindFilter(''),
          },
        ]
      : []),
    ...(statusFilter
      ? [
          {
            key: 'status',
            label: t('geography.filter.status'),
            value: t(
              statusFilter === 'active'
                ? 'geography.filter.status_active'
                : 'geography.filter.status_inactive',
            ),
            onRemove: () => setStatusFilter(''),
          },
        ]
      : []),
  ];

  return (
    <section className="simsm-page simsm-geography">
      <PageHeader
        title={t('geography.title')}
        lede={t('geography.lede', { count: positionCount })}
        actions={
          <>
            <MudButton
              shape="circular"
              variant="neutral"
              size="md"
              onClick={() => {
                resetImport();
                setImportOpen(true);
              }}
            >
              <MudIcon slot="icon-start" name="upload" size={16} />
              {t('geography.action.import')}
            </MudButton>
            <MudButton shape="circular" variant="primary" size="md" onClick={() => openAdd(null)}>
              <MudIcon slot="icon-start" name="plus-large" size={16} />
              {t('geography.action.add')}
            </MudButton>
          </>
        }
      />

      <FilterBar
        search={{
          value: query,
          onChange: setQuery,
          placeholder: t('geography.search.placeholder'),
          ariaLabel: t('geography.search.aria_label'),
          clearLabel: t('common.filter.clear_search'),
        }}
        active={activeFilters}
        onReset={() => {
          setQuery('');
          setParentFilter('');
          setKindFilter('');
          setStatusFilter('');
        }}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('geography.filter.aria_label'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        info={
          <>
            {!filtering && expandedIds.size > 0 ? (
              <MudButton
                shape="circular"
                variant="strict"
                appearance="text"
                size="md"
                onClick={() => setExpandedIds(new Set())}
              >
                {t('geography.action.collapse_all')}
              </MudButton>
            ) : null}
            <MudButton
              shape="circular"
              variant="neutral"
              size="md"
              onClick={() => navigate('/system-admin/geography/history')}
            >
              <MudIcon slot="icon-start" name="clock" size={16} />
              {t('history.action.open_log')}
            </MudButton>
          </>
        }
        panel={
          <>
            {/* Părinte · Statut · Tip, in the wireframe's own order
                (AdminLocalitati.tsx's `panou`). The same picker the form uses,
                so one control answers "which position" everywhere on this page —
                with the whole nomenclator in it, not only what can hold
                subordinates: filtering by a village is a legitimate way to ask
                "is anything under it". */}
            <div className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('geography.filter.parent')}</span>
              <GeographyPicker
                options={filterParentOptions}
                value={parentFilter}
                invalid={false}
                ariaLabel={t('geography.filter.parent')}
                placeholder={t('geography.filter.parent_all')}
                searchPlaceholder={t('geography.picker.search')}
                emptyText={t('geography.picker.empty')}
                moreText={(count) => t('geography.picker.more', { count })}
                onPick={(id) => setParentFilter(id)}
              />
            </div>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('geography.filter.status')}</span>
              <MudSelect
                size="medium"
                value={statusFilter}
                aria-label={t('geography.filter.status')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setStatusFilter(e.detail.value as StatusFilter)
                }
              >
                <option value="">{t('geography.filter.status_all')}</option>
                <option value="active">{t('geography.filter.status_active')}</option>
                <option value="inactive">{t('geography.filter.status_inactive')}</option>
              </MudSelect>
            </label>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('geography.filter.kind')}</span>
              <MudSelect
                size="medium"
                value={kindFilter}
                aria-label={t('geography.filter.kind')}
                onMudChange={(e: CustomEvent<{ value: string }>) => setKindFilter(e.detail.value)}
              >
                <option value="">{t('geography.filter.kind_all')}</option>
                {FILTER_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`geography.kind.${kind}`)}
                  </option>
                ))}
              </MudSelect>
            </label>
          </>
        }
      />

      {/* Only while a filter narrows the tree. Unfiltered, this said exactly what
          the lede two lines above already says — the wireframe prints the position
          count once, in its subtitle, and has no counter row at all
          (AdminLocalitati.tsx:112; parity A-20). Filtered, it says something the
          lede cannot: how much of the nomenclator the criteria left. */}
      {filtering ? (
        <p className="simsm-geo__count" role="status" aria-live="polite">
          {t('geography.count.filtered', {
            shown: [...matches].filter((id) => {
              const node = index.byId.get(id);
              return node ? !isRoot(node) : false;
            }).length,
            total: positionCount,
          })}
        </p>
      ) : null}

      {errorMessage && !form && !importOpen ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {errorMessage}
        </MudInfoBox>
      ) : null}

      {tree.isError ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {t('geography.error.load_failed')}
        </MudInfoBox>
      ) : tree.isLoading ? (
        <p className="simsm-geo__hint">{t('geography.loading')}</p>
      ) : (
        <div className="simsm-geo__table">
          <MudTable
            className="simsm-table"
            columns={columns}
            rows={rows}
            rowStyle="zebra"
            hoverable={true}
            disableSort={true}
            aria-label={t('geography.table.aria_label')}
            // The whole row opens the position's editor, the menu's first entry.
            // The root has no editor (its menu only adds a level-1 unit), so its
            // row opens nothing; the chevron's own click expands, not edits.
            onMudRowClick={onRowOpen((index) => {
              const node = visible[index];
              if (node && !isRoot(node)) openEdit(node);
            })}
          >
            <EmptyRegisterMessage>
              {filtering ? t('geography.empty_filtered') : t('geography.empty_root')}
            </EmptyRegisterMessage>
            <span slot="header-cell-siteCount" title={t('geography.column.sites_title')}>
              {t('geography.column.sites')}
            </span>
            {/* The wireframe heads the row-actions column with an empty cell
                (AdminLocalitati.tsx:138, `<th />`); the register named it, which is
                the only place in the application where a menu column shouts its own
                label. The accessible name stays — the column is still a column to a
                screen reader (parity A-20). */}
            <span slot="header-cell-actions" className="visually-hidden">
              {t('geography.column.actions')}
            </span>
            {visible.map((node, idx) => (
              <span key={`kind-${node.id}`} slot={`cell-kind-${idx}`} className="simsm-geo__kind">
                {t(`geography.kind.${node.kind}`)}
              </span>
            ))}
            {visible.map((node, idx) => (
              <span
                key={`cuatm-${node.id}`}
                slot={`cell-cuatmCode-${idx}`}
                className="simsm-geo__code"
              >
                {node.cuatmCode || EMPTY_CELL}
              </span>
            ))}
            {visible.map((node, idx) => (
              <span
                key={`statistical-${node.id}`}
                slot={`cell-statisticalCode-${idx}`}
                className="simsm-geo__code"
              >
                {node.statisticalCode || EMPTY_CELL}
              </span>
            ))}
            {visible.map((node, idx) => (
              <span key={`name-${node.id}`} slot={`cell-name-${idx}`}>
                <span
                  className="simsm-geo__name"
                  style={{ paddingInlineStart: `${node.depth * INDENT_PX}px` }}
                >
                  {isRoot(node) ? (
                    /* The root gets the mark, not a control: it is the one row
                       that never collapses, and the pin is the same one the
                       navigation uses to name this section. */
                    <span className="simsm-geo__root-mark" aria-hidden="true">
                      <MudIcon name="map-pin" size={16} />
                    </span>
                  ) : node.childCount > 0 ? (
                    <MudButton
                      shape="circular"
                      variant="strict"
                      appearance="text"
                      size="sm"
                      iconOnly
                      disabled={filtering}
                      aria-expanded={filtering || expandedIds.has(node.id)}
                      label={
                        expandedIds.has(node.id)
                          ? t('geography.node.collapse', { name: node.name })
                          : t('geography.node.expand', { name: node.name })
                      }
                      onClick={() => toggleExpand(node.id)}
                    >
                      <MudIcon
                        slot="icon"
                        name={
                          filtering || expandedIds.has(node.id) ? 'chevron-bottom' : 'chevron-right'
                        }
                        size={16}
                      />
                    </MudButton>
                  ) : (
                    <span className="simsm-geo__leaf-spacer" aria-hidden="true" />
                  )}
                  <span className="simsm-geo__name-lines">
                    <span
                      className="simsm-geo__name-text"
                      title={node.name}
                      data-inactive={node.active ? undefined : 'true'}
                      /* The wireframe weights the rows the query actually hit,
                         so the answer stands out from the ancestors carried in
                         to give it a place (AdminLocalitati.tsx:149,
                         `cautaText && potriviri.has(n.cod) ? 'fw500'`). */
                      data-match={searching && matches.has(node.id) ? 'true' : undefined}
                      data-root={isRoot(node) ? 'true' : undefined}
                    >
                      {node.name}
                    </span>
                    {/* The wireframe's second line, drawn only when there is a
                        Russian name to draw (AdminLocalitati.tsx:149 renders its
                        MetadataLine behind `n.numeRu &&`). Same `dl`/`dt`/`dd`
                        markup its MetadataLine emits, colon included. */}
                    {node.nameRu ? (
                      <dl className="simsm-geo__name-ru">
                        <dt>{t('geography.column.name_ru')}:</dt>
                        <dd title={node.nameRu}>{node.nameRu}</dd>
                      </dl>
                    ) : null}
                  </span>
                </span>
              </span>
            ))}
            {visible.map((node, idx) => (
              <span key={`status-${node.id}`} slot={`cell-status-${idx}`}>
                <MudTag
                  variant="status"
                  type={node.active ? 'outlined' : 'subtle'}
                  semantic={node.active ? 'success' : 'muted'}
                  size="sm"
                  label={
                    node.active ? t('geography.status.active') : t('geography.status.inactive')
                  }
                />
              </span>
            ))}
            {visible.map((node, idx) => (
              <span key={`actions-${node.id}`} slot={`cell-actions-${idx}`}>
                <RowActionsMenu
                  label={t('geography.node.actions_for', { name: node.name })}
                  actions={
                    // The root offers one thing: somewhere to put a first-level
                    // unit. It is not renamed, not re-parented (`MoveSubtree`
                    // already refused that) and above all not deactivated — that
                    // cascade takes the whole nomenclator down and reactivation
                    // deliberately does not bring it back, so the "poate fi
                    // reactivată oricând" the old confirm promised was false.
                    // Its own events stay readable in the full change log.
                    isRoot(node)
                      ? [
                          {
                            label: t('geography.node.add_top_level'),
                            onSelect: () => openAdd(node),
                          },
                        ]
                      : [
                          { label: t('geography.node.edit'), onSelect: () => openEdit(node) },
                          {
                            label: t('geography.node.add_child'),
                            onSelect: () => openAdd(node),
                            disabled: !node.active || !PARENT_KINDS.has(node.kind),
                          },
                          { label: t('history.action.open'), onSelect: () => setHistoryNode(node) },
                          node.active
                            ? {
                                label: t('geography.node.deactivate'),
                                danger: true,
                                onSelect: () => void askDeactivate(node),
                                // UC20-16: not while an active site sits in it or under it.
                                disabled: node.activeSiteCount > 0,
                                reason: t('geography.node.deactivate_blocked', {
                                  count: node.activeSiteCount,
                                }),
                              }
                            : {
                                label: t('geography.node.activate'),
                                onSelect: () => toggleActive.mutate(node),
                              },
                        ]
                  }
                />
              </span>
            ))}
          </MudTable>
        </div>
      )}

      <MudModal
        closeLabel={t('common.close')}
        open={form !== null}
        size="md"
        titleText={form?.id ? t('geography.modal.edit.title') : t('geography.modal.add.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 —
        // seven AGE components emit `mudClose` as a bubbling, composed event, so
        // a descendant closing inside this modal reaches this handler too.
        // Without the guard the classifier and parent pickers below take the
        // form down the moment an option is chosen.
        onMudClose={onModalDismiss(() => {
          setForm(null);
          setErrorMessage(null);
        })}
      >
        {form ? (
          <div className="simsm-geo__form">
            {errorMessage ? (
              <MudInfoBox emphasis="strong" role="alert" variant="error">
                {errorMessage}
              </MudInfoBox>
            ) : null}
            {/* Two rows of two, as the wireframe pairs them: the two names, then
                what the position IS and where it SITS (AdminLocalitati.tsx, two
                `grid-2` blocks). Stacked in one column, the four fields read as
                an unrelated list and push the codes below the fold. */}
            <div className="simsm-geo__form-row">
              <MudTextInput
                size="md"
                label={t('geography.field.name')}
                required
                value={form.name}
                invalid={submitted && nameError !== null}
                {...(submitted && nameError ? { errorText: nameError } : {})}
                helperText={t('geography.field.name_hint')}
                onMudInput={(e: CustomEvent<{ value: string }>) =>
                  setForm({ ...form, name: e.detail.value })
                }
              />
              <MudTextInput
                size="md"
                label={t('geography.field.name_ru')}
                value={form.nameRu}
                helperText={t('geography.field.name_ru_hint')}
                placeholder={t('geography.field.name_ru_placeholder')}
                onMudInput={(e: CustomEvent<{ value: string }>) =>
                  setForm({ ...form, nameRu: e.detail.value })
                }
              />
            </div>
            <div className="simsm-geo__form-row">
              <GeographyPicker
                options={kindOptions}
                value={form.kind}
                invalid={false}
                label={t('geography.field.kind')}
                required
                /* No search box: the wireframe marks only the two PARENT
                   pickers `searchable` (AdminLocalitati.tsx:129, :180) and
                   leaves `Tip` and `Statut` plain (:178, :132). Seven kinds do
                   not need a field to filter them, and this picker is used here
                   only because AGE's own select is clipped inside mud-modal. */
                searchable={false}
                placeholder={t('geography.field.kind_placeholder')}
                searchPlaceholder={t('geography.picker.search')}
                emptyText={t('geography.picker.empty')}
                moreText={(count) => t('geography.picker.more', { count })}
                onPick={(kind) => setForm({ ...form, kind })}
              />
              <GeographyPicker
                options={parentOptions}
                value={form.parentId}
                invalid={submitted && parentError !== null}
                label={t('geography.field.parent')}
                required
                placeholder={t('geography.field.parent_placeholder')}
                searchPlaceholder={t('geography.picker.search')}
                emptyText={t('geography.picker.empty')}
                moreText={(count) => t('geography.picker.more', { count })}
                errorText={submitted && parentError ? parentError : undefined}
                helperText={
                  form.id
                    ? t('geography.field.parent_hint_move')
                    : t('geography.field.parent_hint_add')
                }
                onPick={(id) => setForm({ ...form, parentId: id })}
              />
            </div>
          </div>
        ) : null}
        <div slot="actions" className="simsm-geo__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => {
              setForm(null);
              setErrorMessage(null);
            }}
          >
            {t('geography.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={createNode.isPending || saveNode.isPending}
            onClick={submitForm}
          >
            {form?.id ? t('geography.action.save') : t('geography.action.create')}
          </MudButton>
        </div>
      </MudModal>

      <MudModal
        closeLabel={t('common.close')}
        open={importOpen}
        size="md"
        titleText={t('geography.import.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 —
        // seven AGE components emit `mudClose` as a bubbling, composed event, so
        // a descendant closing inside this modal reaches this handler too. The
        // info box and the notifications below are all emitters, so any of them
        // would take this modal with it.
        onMudClose={onModalDismiss(() => {
          setImportOpen(false);
          setErrorMessage(null);
        })}
      >
        <div className="simsm-geo__form">
          <MudStepper
            currentStep={importStep}
            aria-label={t('geography.import.steps.aria')}
            steps={[
              { label: t('geography.import.step.choose'), status: importStepStatus(0, importStep) },
              {
                label: t('geography.import.step.preview'),
                status: importStepStatus(1, importStep),
              },
              { label: t('geography.import.step.result'), status: importStepStatus(2, importStep) },
            ]}
          />

          {errorMessage ? (
            <MudInfoBox emphasis="strong" role="alert" variant="error">
              {errorMessage}
            </MudInfoBox>
          ) : null}

          {importStep === 0 ? (
            <>
              <MudInfoBox
                variant="info"
                emphasis="subtle"
                titleText={t('geography.import.lede_title')}
              >
                {t('geography.import.lede')}
              </MudInfoBox>
              <MudButton
                shape="circular"
                variant="neutral"
                size="sm"
                onClick={() => window.open(CUATM_TEMPLATE_URL, '_blank')}
              >
                <MudIcon slot="icon-start" name="download" size={16} />
                {t('geography.import.template')}
              </MudButton>
              <MudFileInput
                accept={GEOGRAPHY_IMPORT_ACCEPT}
                onMudChange={(e: CustomEvent<{ files: File[] }>) => {
                  setImportFile(e.detail.files[0] ?? null);
                  setImportPreview(null);
                  setImportOutcome(null);
                  setOverwriteCodes(new Set());
                }}
              />
            </>
          ) : null}

          {importReport && importFile ? (
            <MudFileItem
              removeLabel={t('common.file_input.remove')}
              filename={importFile.name}
              size={importFile.size}
              state="success"
              noRemove={doImport.isPending}
              onMudRemove={resetImport}
            />
          ) : null}

          {importPreview && importPreview.errors.length > 0 ? (
            <div className="simsm-geo__import-errors">
              <MudInfoBox emphasis="strong" role="alert" variant="warning">
                {/* Rows, not messages: one row can break two rules. */}
                {t('geography.import.errors', {
                  count: new Set(importPreview.errors.map((err) => err.rowIndex)).size,
                })}
              </MudInfoBox>
              <ul className="simsm-geo__import-error-list">
                {importPreview.errors.slice(0, 10).map((err) => (
                  <li key={`${err.rowIndex}-${err.column}-${err.code}-${err.message}`}>
                    {t('geography.import.error_row', {
                      row: err.rowIndex,
                      column: err.column,
                      message: importErrorMessage(err),
                    })}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {importSummary ? (
            <>
              {/* The wireframe's four buckets, in its order: what the file adds,
                  what it renames, what it re-parents, and what it drops. */}
              <div className="simsm-import__tiles">
                <div className="simsm-import__tile">
                  <span className="simsm-import__tile-key">
                    {t('geography.import.tile.created')}
                  </span>
                  <span className="simsm-import__tile-value">{importSummary.created}</span>
                </div>
                <div className="simsm-import__tile">
                  <span className="simsm-import__tile-key">
                    {t('geography.import.tile.renamed')}
                  </span>
                  <span className="simsm-import__tile-value">{importSummary.renamed}</span>
                </div>
                <div className="simsm-import__tile">
                  <span className="simsm-import__tile-key">{t('geography.import.tile.moved')}</span>
                  <span className="simsm-import__tile-value">{importSummary.moved}</span>
                </div>
                <div className="simsm-import__tile">
                  <span className="simsm-import__tile-key">
                    {t('geography.import.tile.vanished')}
                  </span>
                  <span className="simsm-import__tile-value">{importSummary.vanished}</span>
                </div>
              </div>
              <p className="simsm-import__hint">
                {t('geography.import.rows_read', {
                  received: importSummary.received,
                  unchanged: importSummary.skipped,
                })}
                {importReport?.format
                  ? ` ${t(`geography.import.format.${importReport.format}`)}`
                  : ''}
              </p>
            </>
          ) : null}

          {/* Only the preview warns: at the result step the deactivation has
              already happened, and the success notification says how many. */}
          {importOutcome === null && importSummary && importSummary.vanished > 0 ? (
            <MudInfoBox
              emphasis="strong"
              variant="warning"
              titleText={t('geography.import.vanished.title')}
            >
              {t('geography.import.vanished.body', { count: importSummary.vanished })}
              {importSummary.vanishedSample.length > 0 ? (
                <ul className="simsm-geo__import-error-list">
                  {importSummary.vanishedSample.map((position) => (
                    <li key={position.cuatmCode}>
                      {position.name} · {t(`geography.kind.${position.kind}`)} ·{' '}
                      {position.cuatmCode}
                    </li>
                  ))}
                  {importSummary.vanished > importSummary.vanishedSample.length ? (
                    <li>
                      {t('geography.import.vanished.more', {
                        count: importSummary.vanished - importSummary.vanishedSample.length,
                      })}
                    </li>
                  ) : null}
                </ul>
              ) : null}
            </MudInfoBox>
          ) : null}

          {/* UC20-16: the preview names every manually edited position the file would
              change. Unticked keeps the manual values; the rest of the file applies. */}
          {importOutcome === null && importSummary && importSummary.manualConflicts.length > 0 ? (
            <MudInfoBox
              emphasis="strong"
              variant="warning"
              titleText={t('geography.import.manual.title')}
            >
              {t('geography.import.manual.body', {
                count: importSummary.manualConflicts.length,
              })}
              <div className="simsm-geo__import-conflicts">
                {importSummary.manualConflicts.map((conflict) => (
                  <div key={conflict.cuatmCode} className="simsm-geo__import-conflict">
                    <span className="simsm-geo__import-conflict-name">
                      {conflict.name} · {t(`geography.kind.${conflict.kind}`)} ·{' '}
                      {conflict.cuatmCode}
                    </span>
                    <ul className="simsm-geo__import-error-list">
                      {conflict.fields.map((change) => (
                        <li key={change.field}>
                          {t('geography.import.manual.change', {
                            field: t(`geography.import.manual.field.${change.field}`),
                            current: changeValue(change.field, change.current),
                            incoming: changeValue(change.field, change.incoming),
                          })}
                        </li>
                      ))}
                    </ul>
                    <MudCheckbox
                      size="md"
                      checked={overwriteCodes.has(conflict.cuatmCode)}
                      disabled={doImport.isPending}
                      onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                        toggleOverwrite(conflict.cuatmCode, e.detail.checked)
                      }
                    >
                      <span slot="label">
                        {t('geography.import.manual.apply', { name: conflict.name })}
                      </span>
                    </MudCheckbox>
                  </div>
                ))}
              </div>
            </MudInfoBox>
          ) : null}

          {importOutcome === null && importSummary && importSummary.vanishedHeld > 0 ? (
            <MudInfoBox
              emphasis="strong"
              variant="info"
              titleText={t('geography.import.held.title')}
            >
              {t('geography.import.held.body', { count: importSummary.vanishedHeld })}
              <ul className="simsm-geo__import-error-list">
                {importSummary.vanishedHeldSample.map((position) => (
                  <li key={position.cuatmCode}>
                    {t('geography.import.held.item', {
                      name: position.name,
                      kind: t(`geography.kind.${position.kind}`),
                      code: position.cuatmCode,
                      count: position.activeSites,
                    })}
                  </li>
                ))}
                {importSummary.vanishedHeld > importSummary.vanishedHeldSample.length ? (
                  <li>
                    {t('geography.import.vanished.more', {
                      count: importSummary.vanishedHeld - importSummary.vanishedHeldSample.length,
                    })}
                  </li>
                ) : null}
              </ul>
            </MudInfoBox>
          ) : null}

          {importOutcome?.summary ? (
            <div className="simsm-inline-outcome">
              <MudToast closable={false} variant="success">
                {t('geography.import.success', {
                  created: importOutcome.summary.created,
                  updated: importOutcome.summary.updated,
                  deactivated: importOutcome.summary.deactivated,
                })}
                {importOutcome.summary.manualConflicts.length > 0
                  ? ` ${t('geography.import.result.manual', {
                      kept: importOutcome.summary.manualKept,
                      overwritten: importOutcome.summary.manualOverwritten,
                    })}`
                  : ''}
                {importOutcome.summary.vanishedHeld > 0
                  ? ` ${t('geography.import.result.held', {
                      count: importOutcome.summary.vanishedHeld,
                    })}`
                  : ''}
              </MudToast>
            </div>
          ) : null}
        </div>
        <div slot="actions" className="simsm-geo__modal-actions">
          {importStep === 2 ? (
            <MudButton
              shape="circular"
              variant="primary"
              size="md"
              onClick={() => {
                setImportOpen(false);
                resetImport();
              }}
            >
              {t('common.close')}
            </MudButton>
          ) : (
            <>
              <MudButton
                shape="circular"
                variant="neutral"
                size="md"
                disabled={importBusy}
                onClick={() => {
                  if (importStep === 1) {
                    resetImport();
                    return;
                  }
                  setImportOpen(false);
                  setErrorMessage(null);
                }}
              >
                {importStep === 1
                  ? t('geography.import.action.other_file')
                  : t('geography.action.cancel')}
              </MudButton>
              <MudButton
                shape="circular"
                variant="primary"
                size="md"
                disabled={!importFile || importBusy || (importStep === 1 && !importSummary)}
                onClick={() => {
                  if (!importFile) return;
                  setErrorMessage(null);
                  if (importStep === 1) {
                    doImport.mutate(importFile);
                    return;
                  }
                  validateImport.mutate(importFile);
                }}
              >
                {importBusy ? (
                  <MudSpinner slot="icon-start" size="sm" label={t('common.loading')} />
                ) : null}
                {importStep === 1
                  ? t('geography.import.submit')
                  : t('geography.import.action.validate')}
              </MudButton>
            </>
          )}
        </div>
      </MudModal>

      <HistoryModal
        open={historyNode !== null}
        onClose={() => setHistoryNode(null)}
        kind="GEOGRAPHY"
        entityId={historyNode?.id ?? null}
        entityLabel={historyNode?.name ?? ''}
      />
    </section>
  );
}
